import React, { useState, useMemo } from "react";
import { useTheme } from "@mui/material/styles";

const PAGE_SIZE = 10;

// ── useBreakpoint ─────────────────────────────────────────────────────────────
const useBreakpoint = () => {
  const [width, setWidth] = React.useState(
    typeof window !== "undefined" ? window.innerWidth : 1200
  );
  React.useEffect(() => {
    const h = () => setWidth(window.innerWidth);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);
  if (width < 480)  return "xs";
  if (width < 768)  return "sm";
  if (width < 1024) return "md";
  return "lg";
};

// ── FTE pill ──────────────────────────────────────────────────────────────────
const FtePill = ({ fte, isDark }) => {
  const map = {
    1.0:  { bg: isDark ? "rgba(40,179,180,0.2)"  : "rgba(40,179,180,0.12)", color: isDark ? "#9FE1CB" : "#085041" },
    0.75: { bg: isDark ? "rgba(214,223,39,0.2)"  : "rgba(214,223,39,0.14)", color: isDark ? "#D6DF27" : "#6B7300" },
    0.5:  { bg: isDark ? "rgba(55,138,221,0.2)"  : "rgba(55,138,221,0.12)", color: isDark ? "#85B7EB" : "#0C447C" },
    0.25: { bg: isDark ? "rgba(226,75,74,0.2)"   : "rgba(226,75,74,0.1)",  color: isDark ? "#F09595" : "#791F1F" },
  };
  const s = map[fte] || { bg: isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.06)", color: isDark ? "#C8E8E6" : "#4A7A78" };
  return (
    <span style={{
      display:"inline-flex", alignItems:"center", justifyContent:"center",
      fontSize:11, fontWeight:700, padding:"3px 9px",
      borderRadius:20, background:s.bg, color:s.color,
      letterSpacing:".02em", whiteSpace:"nowrap",
    }}>
      {fte}
    </span>
  );
};

// ── Status chip ───────────────────────────────────────────────────────────────
const StatusChip = ({ status, isDark }) => {
  const map = {
    ACTIVE:     { bg: isDark ? "rgba(40,179,180,0.2)"  : "rgba(40,179,180,0.1)",  color: isDark ? "#9FE1CB" : "#085041", dot: "#28B3B4" },
    ENDED:      { bg: isDark ? "rgba(214,223,39,0.18)" : "rgba(214,223,39,0.12)", color: isDark ? "#D6DF27" : "#6B7300", dot: "#D6DF27" },
    SUPERSEDED: { bg: isDark ? "rgba(226,75,74,0.18)"  : "rgba(226,75,74,0.08)", color: isDark ? "#F09595" : "#791F1F", dot: "#E24B4A" },
  };
  const s = map[status] || map.ENDED;
  return (
    <span style={{
      display:"inline-flex", alignItems:"center", gap:5,
      fontSize:11, fontWeight:600, padding:"3px 9px",
      borderRadius:20, background:s.bg, color:s.color, whiteSpace:"nowrap",
    }}>
      <span style={{ width:5, height:5, borderRadius:"50%", background:s.dot, flexShrink:0 }} />
      {status}
    </span>
  );
};

// ── MAC tag ───────────────────────────────────────────────────────────────────
const MacTag = ({ code, isDark, accent, accent2 }) => (
  <span style={{
    display:"inline-block",
    fontSize:11, fontWeight:700,
    padding:"2px 8px", borderRadius:6,
    background: accent2,
    color: accent,
    letterSpacing:".04em",
    border: `1px solid ${isDark ? "rgba(40,179,180,0.3)" : "rgba(40,179,180,0.2)"}`,
  }}>
    {code}
  </span>
);

// ── Sort icon ─────────────────────────────────────────────────────────────────
const SortIcon = ({ colKey, sortKey, sortDir, color }) => {
  const isActive = sortKey === colKey;
  const upColor   = isActive && sortDir === "asc"  ? color : "rgba(128,128,128,0.3)";
  const downColor = isActive && sortDir === "desc" ? color : "rgba(128,128,128,0.3)";
  return (
    <span style={{ display:"inline-flex", flexDirection:"column", gap:1, marginLeft:5, verticalAlign:"middle" }}>
      <svg width="7" height="5" viewBox="0 0 7 5"><path d="M3.5 0L7 5H0z" fill={upColor} /></svg>
      <svg width="7" height="5" viewBox="0 0 7 5"><path d="M3.5 5L0 0H7z" fill={downColor} /></svg>
    </span>
  );
};

// ── Pagination button ─────────────────────────────────────────────────────────
const PgBtn = ({ children, active, disabled, onClick, t, isDark }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    style={{
      minWidth:30, height:30, padding:"0 6px",
      borderRadius:8,
      border: active
        ? `1.5px solid ${t.accent}`
        : `1px solid ${t.border2}`,
      background: active
        ? isDark ? "rgba(40,179,180,0.18)" : "rgba(40,179,180,0.1)"
        : "transparent",
      color: active ? t.accent : disabled ? t.text3 : t.text2,
      fontSize:12, fontWeight: active ? 700 : 400,
      cursor: disabled ? "not-allowed" : "pointer",
      opacity: disabled ? 0.4 : 1,
      transition:"all .15s",
    }}
  >
    {children}
  </button>
);

// ── SORTABLE COLUMNS config ───────────────────────────────────────────────────
// Maps a column key → how to extract a comparable value from a row.
// String fields → lowercased for case-insensitive sort.
// Date fields → converted to timestamps for correct chronological order.
const SORT_CONFIG = {
  employeeId: row => (row.employeeId || "").toLowerCase(),
  associate: row => (row.associateName || "").toLowerCase(),
  mac:       row => (row.macCode || "").toLowerCase(),
  sow:       row => (row.sowCode || "").toLowerCase(),
  position:  row => (row.positionCode || "").toLowerCase(),
  fte:       row => row.fte ?? 0,
  startDate: row => new Date(row.startDate).getTime() || 0,
  endDate:   row => new Date(row.endDate).getTime() || 0,
};

// ── AllocationTable ───────────────────────────────────────────────────────────
const AllocationTable = ({ allocations = [] }) => {
  const theme  = useTheme();
  const t      = theme.custom;
  const isDark = theme.palette.mode === "dark";
  const bp     = useBreakpoint();

  // ── search & sort state ──
  const [search,  setSearch]  = useState("");
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const [page,    setPage]    = useState(1);

  const showSow      = bp !== "xs";
  const showPosition = bp === "lg" || bp === "md";
  const showEmail    = bp === "lg";

  // ── handleSort: toggle direction if same col, else set new col asc ──
  const handleSort = (key) => {
    setSortKey(prev => {
      if (prev === key) {
        setSortDir(d => d === "asc" ? "desc" : "asc");
        return key;
      }
      setSortDir("asc");
      return key;
    });
    setPage(1); // reset to first page on sort change
  };

  // ── filtered + sorted data (recalculated only when deps change) ──
  const processed = useMemo(() => {
    const q = search.trim().toLowerCase();

    // 1. filter
    let result = q
      ? allocations.filter(row =>
          [
            row.associateName, row.associateEmail,
            row.macCode, row.sowCode, row.sowName,
            row.positionCode, row.positionType,
            String(row.fte), row.startDate, row.endDate, row.status,
          ]
            .filter(Boolean)
            .some(v => v.toLowerCase().includes(q))
        )
      : allocations;

    // 2. sort
    if (sortKey && SORT_CONFIG[sortKey]) {
      const getter = SORT_CONFIG[sortKey];
      result = [...result].sort((a, b) => {
        const av = getter(a);
        const bv = getter(b);
        if (av < bv) return sortDir === "asc" ? -1 : 1;
        if (av > bv) return sortDir === "asc" ?  1 : -1;
        return 0;
      });
    }

    return result;
  }, [allocations, search, sortKey, sortDir]);

  // ── pagination on processed data ──
  const totalPages = Math.max(1, Math.ceil(processed.length / PAGE_SIZE));
  const rows       = processed.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // reset page to 1 when search changes
  const handleSearch = (val) => {
    setSearch(val);
    setPage(1);
  };

  const pageNums = () => {
    if (totalPages <= 5) return Array.from({ length: totalPages }, (_, i) => i + 1);
    if (page <= 3) return [1, 2, 3, 4, 5];
    if (page >= totalPages - 2) return [totalPages-4, totalPages-3, totalPages-2, totalPages-1, totalPages];
    return [page-2, page-1, page, page+1, page+2];
  };

  // ── shared styles ──
  const th = {
    fontSize:11, fontWeight:600, letterSpacing:".05em", textTransform:"uppercase",
    color:t.text3, padding:"10px 12px", textAlign:"left",
    borderBottom:`1.5px solid ${t.border2}`,
    whiteSpace:"nowrap",
    background: t.surface,
    position:"sticky", top:0, zIndex:2,
    userSelect:"none",
    cursor:"pointer",
  };
  const td = {
    fontSize:12, color:t.text, padding:"10px 12px",
    borderBottom:`1px solid ${t.border}`,
    verticalAlign:"middle",
  };

  // ── helper to build a sortable <th> ──
  const Th = ({ colKey, style = {}, children }) => (
    <th
      style={{ ...th, ...style }}
      onClick={() => handleSort(colKey)}
      title={`Sort by ${colKey}`}
    >
      {children}
      <SortIcon colKey={colKey} sortKey={sortKey} sortDir={sortDir} color={t.accent} />
    </th>
  );

  return (
    <div style={{
      background: t.surface,
      border: `1px solid ${t.border}`,
      borderRadius: 12,
      display: "flex",
      flexDirection: "column",
      maxHeight: 520,
      minHeight: 0,
    }}>

      {/* ── card header ── */}
      <div style={{
        display:"flex", alignItems:"center", justifyContent:"space-between",
        padding:"14px 18px",
        borderBottom:`1px solid ${t.border}`,
        flexShrink: 0,
        borderRadius: "12px 12px 0 0",
        background: t.surface,
        zIndex: 3,
        gap: 12,
        flexWrap: "wrap",
      }}>
        {/* left: title + count badge */}
        <div style={{ display:"flex", alignItems:"center", gap:10, flexShrink:0 }}>
          <span style={{ fontSize:13, fontWeight:700, color:t.text }}>Allocations</span>
          <span style={{
            fontSize:11, fontWeight:600, padding:"2px 8px",
            borderRadius:20, background:t.accent2, color:t.accent,
          }}>
            {processed.length === allocations.length
              ? `${allocations.length} records`
              : `${processed.length} / ${allocations.length}`}
          </span>
        </div>

        {/* right: search box */}
        <div style={{ position:"relative", flexGrow:1, maxWidth:280 }}>
          {/* search icon */}
          <svg
            width="13" height="13" viewBox="0 0 16 16"
            style={{ position:"absolute", left:9, top:"50%", transform:"translateY(-50%)", pointerEvents:"none", opacity:.5 }}
          >
            <circle cx="6.5" cy="6.5" r="5" stroke={t.text3} strokeWidth="1.5" fill="none"/>
            <line x1="10.5" y1="10.5" x2="14" y2="14" stroke={t.text3} strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
          <input
            type="text"
            value={search}
            onChange={e => handleSearch(e.target.value)}
            placeholder="Search allocations…"
            style={{
              width:"100%", boxSizing:"border-box",
              height:30, paddingLeft:28, paddingRight: search ? 28 : 10,
              fontSize:12, color:t.text,
              background: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)",
              border:`1px solid ${t.border2}`,
              borderRadius:8, outline:"none",
              transition:"border-color .15s",
            }}
            onFocus={e  => e.target.style.borderColor = t.accent}
            onBlur={e   => e.target.style.borderColor = t.border2}
          />
          {/* clear button */}
          {search && (
            <button
              onClick={() => handleSearch("")}
              style={{
                position:"absolute", right:6, top:"50%", transform:"translateY(-50%)",
                background:"none", border:"none", cursor:"pointer",
                color:t.text3, fontSize:14, lineHeight:1, padding:2,
                display:"flex", alignItems:"center",
              }}
              title="Clear search"
            >
              ×
            </button>
          )}
        </div>

        <div style={{ height:3, width:32, borderRadius:2, background:t.accent, flexShrink:0 }} />
      </div>

      {/* ── scrollable table area ── */}
      <div style={{ overflowX:"auto", overflowY:"auto", flex:1, minHeight:0 }}>
        <table style={{
          width:"100%", borderCollapse:"collapse",
          minWidth: bp === "xs" ? 400 : 600,
        }}>
          <thead>
            <tr>
              <Th colKey="associate">Associate</Th>
              <Th colKey="employeeId">Emp ID</Th>
              <Th colKey="mac">MAC</Th>
{showSow && <Th colKey="sow">SOW</Th>}
              {showPosition && <Th colKey="position">Position</Th>}
              <Th colKey="fte" style={{ textAlign:"center" }}>Allocated FTE (Prorated)</Th>
              <Th colKey="startDate">Start</Th>
              <Th colKey="endDate">End</Th>
              <th style={{ ...th, cursor:"default" }}>Balance Cost Attr.</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td
                  colSpan={6 + (showSow ? 1 : 0) + (showPosition ? 1 : 0) + (showEmail ? 1 : 0)}
                  style={{ ...td, textAlign:"center", padding:"40px 20px" }}
                >
                  <div style={{ fontSize:28, marginBottom:10, opacity:.4 }}>📋</div>
                  <div style={{ fontSize:13, color:t.text3 }}>
                    {search
                      ? `No allocations match "${search}"`
                      : "No allocations found for the selected filters"}
                  </div>
                </td>
              </tr>
            ) : (
              rows.map((row, idx) => (
                <tr
                  key={row.requestId}
                  style={{ background: idx % 2 === 0 ? "transparent" : isDark ? "rgba(255,255,255,0.02)" : "rgba(3,81,77,0.02)" }}
                  onMouseEnter={e => e.currentTarget.style.background = isDark ? "rgba(40,179,180,0.06)" : "rgba(40,179,180,0.04)"}
                  onMouseLeave={e => e.currentTarget.style.background = idx % 2 === 0 ? "transparent" : isDark ? "rgba(255,255,255,0.02)" : "rgba(3,81,77,0.02)"}
                >
                  <td style={td}>
                    <div style={{ fontWeight:600, fontSize:12, color:t.text, marginBottom:2 }}>
                      {row.associateName}
                    </div>
                    {showEmail && (
                      <div style={{ fontSize:11, color:t.text3 }}>{row.associateEmail}</div>
                    )}
                  </td>
                  <td style={td}>
  <span style={{
    fontSize:11, fontWeight:700, fontFamily:"monospace",
    color: t.accent,
  }}>
    {row.employeeId}
  </span>
</td>
                  <td style={td}>
  <MacTag code={row.macCode} isDark={isDark} accent={t.accent} accent2={t.accent2} />
</td>
{showSow && (
                    <td style={td}>
                      <div style={{ fontWeight:600, fontSize:11, color:t.text, marginBottom:2 }}>{row.sowCode}</div>
                      <div style={{ fontSize:11, color:t.text3 }}>{row.sowName}</div>
                    </td>
                  )}
                  {showPosition && (
                    <td style={td}>
                      <div style={{ fontSize:11, color:t.text, marginBottom:2, fontFamily:"monospace" }}>{row.positionCode}</div>
                      <span style={{
                        fontSize:10, fontWeight:700, padding:"1px 6px", borderRadius:4,
                        background: isDark ? t.surface2 : t.surface3,
                        color:t.text2, letterSpacing:".04em",
                      }}>
                        {row.positionType}
                      </span>
                    </td>
                  )}
                  <td style={{ ...td, textAlign:"center" }}>
                    <FtePill fte={row.fte} isDark={isDark} />
                  </td>
                  <td style={{ ...td, fontVariantNumeric:"tabular-nums", fontSize:11, color:t.text2, whiteSpace:"nowrap" }}>
                    {row.startDate}
                  </td>
                  <td style={{ ...td, fontVariantNumeric:"tabular-nums", fontSize:11, color:t.text2, whiteSpace:"nowrap" }}>
                    {row.endDate}
                  </td>
                  <td style={td}>
                    {row.balanceCostAttributedTo
                      ? <span style={{
                          fontSize:11, fontWeight:700,
                          fontFamily:"'DM Mono', monospace",
                          color: row.balanceCostAttributedTo.startsWith("TSG") ? t.gold : t.accent,
                        }}>
                          {row.balanceCostAttributedTo}
                        </span>
                      : <span style={{ fontSize:11, color:t.text3 }}>—</span>
                    }
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* ── pagination ── */}
      {processed.length > PAGE_SIZE && (
        <div style={{
          display:"flex", alignItems:"center",
          justifyContent: bp === "xs" ? "center" : "space-between",
          padding:"10px 16px",
          borderTop:`1px solid ${t.border}`,
          flexWrap:"wrap", gap:8,
          flexShrink: 0,
        }}>
          {bp !== "xs" && (
            <span style={{ fontSize:12, color:t.text3 }}>
              Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, processed.length)} of {processed.length}
            </span>
          )}
          <div style={{ display:"flex", alignItems:"center", gap:4 }}>
            <PgBtn onClick={() => setPage(1)}          disabled={page === 1}          t={t} isDark={isDark}>«</PgBtn>
            <PgBtn onClick={() => setPage(p => p - 1)} disabled={page === 1}          t={t} isDark={isDark}>‹</PgBtn>
            {pageNums().map(n => (
              <PgBtn key={n} onClick={() => setPage(n)} active={page === n} t={t} isDark={isDark}>{n}</PgBtn>
            ))}
            <PgBtn onClick={() => setPage(p => p + 1)} disabled={page === totalPages} t={t} isDark={isDark}>›</PgBtn>
            <PgBtn onClick={() => setPage(totalPages)} disabled={page === totalPages} t={t} isDark={isDark}>»</PgBtn>
          </div>
        </div>
      )}
    </div>
  );
};

export default AllocationTable;